import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { EmbeddedViewer } from "./embedded-viewer";
import type { MountViewer } from "./types";
import styles from "./viewer.module.css";

const PATIENT_STATE_LABEL: Record<string, string> = {
  approved: "viewer.state.approved",
  pending: "viewer.state.pending",
  rework: "viewer.state.rework",
  not_approved: "viewer.state.not_approved",
};

export function PatientPlanPage() {
  const { token } = useParams<{ token: string }>();
  const intl = useIntl();
  const [plan, setPlan] = useState<{ patient_id: number; correction_index: number; version: number; state?: string; manifest_url: string } | null>(null);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setPlan(null);
    setFailed(false);
    void fetch(`${process.env.API}/public/viewer-plans/${encodeURIComponent(token)}`, { signal: controller.signal, credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer" })
      .then(async (response) => {
        if (!response.ok) throw new Error("Plan unavailable");
        const data = (await response.json()) as { patient_id: number; correction_index: number; version: number; state?: string; manifest_url: string };
        const manifest = new URL(data.manifest_url, window.location.href);
        if (manifest.origin !== window.location.origin) throw new Error("Invalid manifest origin");
        if (!controller.signal.aborted) setPlan({ ...data, manifest_url: manifest.href });
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [token, revision]);
  const options = useMemo<Parameters<MountViewer>[1] | null>(
    () =>
      plan
        ? {
            manifestUrl: plan.manifest_url,
            version: plan.version,
            patientId: plan.patient_id,
            correctionIndex: plan.correction_index,
            lang: intl.locale === "en" ? "en" : "ru",
            actions: { approve: false, rework: false, report: false, close: false },
            handlers: { approve: async () => {}, rework: async () => {}, report: () => {}, close: () => {} },
          }
        : null,
    [plan, intl.locale],
  );
  const stateLabel = plan?.state ? PATIENT_STATE_LABEL[plan.state] : undefined;
  return (
    <main className={styles["patientPlan"]}>
      {failed ? (
        <div role="alert">
          <p>{intl.formatMessage({ id: "viewer.public.unavailable" })}</p>
          <button type="button" onClick={() => setRevision((value) => value + 1)}>
            {intl.formatMessage({ id: "viewer.retry" })}
          </button>
        </div>
      ) : null}
      {!failed && !options ? <p role="status">{intl.formatMessage({ id: "viewer.loading" })}</p> : null}
      {stateLabel ? <p className={styles["patientState"]}>{intl.formatMessage({ id: stateLabel })}</p> : null}
      {options ? <EmbeddedViewer options={options} /> : null}
    </main>
  );
}
